import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { galleryImages } from "@/mocks/gallery";

// ノーマルカテゴリの画像のみ使用
const heroSlides = galleryImages.filter((img) => img.category === "normal");

export default function HeroSection() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  const goToNext = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % heroSlides.length);
      setIsTransitioning(false);
    }, 600);
  }, [isTransitioning]);

  const goToPrev = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
      setIsTransitioning(false);
    }, 600);
  }, [isTransitioning]);

  // 自動スライド
  useEffect(() => {
    const timer = setInterval(goToNext, 4000);
    return () => clearInterval(timer);
  }, [goToNext]);

  const handleBooking = () => {
    if (window.innerWidth < 768 && location.pathname === "/") {
      const el = document.getElementById("contact");
      if (el) { el.scrollIntoView({ behavior: "smooth" }); return; }
    }
    navigate("/reservation");
  };

  const scrollToAbout = () => {
    const el = document.getElementById("about");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="home" className="min-h-screen flex flex-col items-center justify-center bg-stone-50 pt-14 md:pt-28 pb-6 px-4">

      {/* ===== タイトル エリア（文字のみ） ===== */}
      <div className="text-center mb-4 md:mb-5">
        {/* サブタイトル */}
        <p
          className="text-amber-800 text-xs md:text-sm tracking-[0.4em] mb-2 font-light"
          style={{ fontFamily: "'Noto Serif JP', serif" }}
        >
          SM・M性感 個人セッション
        </p>

        {/* メインタイトル */}
        <h1
          className="text-stone-900 leading-none mb-2"
          style={{
            fontFamily: "'Noto Serif JP', serif",
            fontSize: "clamp(1.6rem, 4vw, 3rem)",
            fontWeight: 200,
            letterSpacing: "0.25em",
          }}
        >
          むぎ茶
        </h1>

        {/* ボトムライン */}
        <div className="flex items-center justify-center gap-4 mb-2">
          <div className="w-8 h-px bg-amber-500/80"></div>
          <div className="w-1.5 h-1.5 bg-amber-500/80 rotate-45"></div>
          <div className="w-8 h-px bg-amber-500/80"></div>
        </div>

        {/* 英語クレジット */}
        <p
          className="text-stone-500 text-xs md:text-sm tracking-[0.45em] font-light"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          By Mugi-cha production
        </p>
      </div>

      {/* ===== ギャラリースライドショー ===== */}
      <div className="w-full max-w-4xl mx-auto mb-4 md:mb-5">
        {/* メインスライダー */}
        <div className="relative overflow-hidden">
          {/* 複数画像を横並び表示 */}
          <div className="flex items-center justify-center gap-3 md:gap-4">
            {/* メイン画像（横長・全幅） */}
            <div
              className="relative w-full"
              style={{ aspectRatio: "16/9" }}
            >
              <img
                src={heroSlides[currentIndex].src}
                alt={heroSlides[currentIndex].alt}
                className={`w-full h-full object-cover object-center transition-opacity ${
                  isTransitioning ? "opacity-0" : "opacity-100"
                }`}
                style={{ transitionDuration: "600ms" }}
              />
            </div>
          </div>

          {/* スワイプ操作 */}
          <div
            className="absolute inset-0"
            onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
            onTouchMove={(e) => { touchEndX.current = e.touches[0].clientX; }}
            onTouchEnd={() => {
              if (touchStartX.current === null || touchEndX.current === null) return;
              const diff = touchStartX.current - touchEndX.current;
              if (Math.abs(diff) > 40) {
                if (diff > 0) goToNext();
                else goToPrev();
              }
              touchStartX.current = null;
              touchEndX.current = null;
            }}
          />

          {/* 矢印ボタン */}
          <button
            onClick={goToPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-700 transition-colors duration-200 cursor-pointer"
            aria-label="前の画像"
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>
          <button
            onClick={goToNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-700 transition-colors duration-200 cursor-pointer"
            aria-label="次の画像"
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>
        </div>

        {/* ドットインジケーター */}
        <div className="flex justify-center gap-1.5 mt-3">
          {heroSlides.map((_, i) => (
            <button
              key={i}
              onClick={() => {
                if (!isTransitioning) {
                  setIsTransitioning(true);
                  setTimeout(() => {
                    setCurrentIndex(i);
                    setIsTransitioning(false);
                  }, 400);
                }
              }}
              className={`transition-all duration-300 cursor-pointer ${
                i === currentIndex
                  ? "w-6 h-1 bg-amber-500"
                  : "w-1 h-1 bg-stone-300 hover:bg-stone-500"
              }`}
              aria-label={`画像 ${i + 1}`}
            />
          ))}
        </div>
      </div>

      {/* ===== CTAボタン ===== */}
      <div className="flex flex-col sm:flex-row gap-3 items-center">
        <button
          onClick={handleBooking}
          className="group flex items-center justify-center gap-2.5 px-8 py-3 bg-amber-600 hover:bg-amber-500 text-white text-sm tracking-[0.25em] font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-calendar-check-line text-sm"></i>
          </div>
          ご予約はこちら
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-right-line text-xs transition-transform duration-300 group-hover:translate-x-1"></i>
          </div>
        </button>

        <button
          onClick={scrollToAbout}
          className="flex items-center justify-center gap-2 px-8 py-3 border border-stone-300 hover:border-stone-500 text-stone-500 hover:text-stone-800 text-sm tracking-[0.25em] font-light transition-colors duration-300 cursor-pointer whitespace-nowrap"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-user-heart-line text-sm"></i>
          </div>
          むぎ茶について
        </button>
      </div>

      {/* おはなしコースへの誘導リンク */}
      <div className="mt-4 flex flex-col items-center gap-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-px bg-stone-200"></div>
          <span className="text-[10px] tracking-[0.35em] text-stone-300 uppercase">or</span>
          <div className="w-10 h-px bg-stone-200"></div>
        </div>
        <button
          onClick={() => {
            const el = document.getElementById("price");
            if (el) el.scrollIntoView({ behavior: "smooth" });
            setTimeout(() => {
              const talkEl = document.querySelector("[data-section='talk-course']");
              if (talkEl) talkEl.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 600);
          }}
          className="group flex flex-col items-center gap-1.5 px-5 py-2.5 border border-stone-100 hover:border-amber-300/50 bg-transparent hover:bg-amber-50/40 rounded-sm transition-all duration-300 cursor-pointer"
        >
          <div className="flex items-center gap-2.5 text-stone-500 group-hover:text-amber-600 transition-colors duration-300">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-chat-heart-line text-sm"></i>
            </div>
            <span className="text-[13px] tracking-[0.3em] font-light">おはなしコース</span>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-chat-heart-line text-sm opacity-0"></i>
            </div>
          </div>
          <span className="text-[11px] tracking-[0.15em] text-stone-400 group-hover:text-amber-500/80 transition-colors duration-300">カウンセリングセッションのご用意もあります</span>
          <div className="w-3 h-3 flex items-center justify-center text-stone-300 group-hover:text-amber-400 mt-0.5 transition-all duration-300 group-hover:translate-y-0.5">
            <i className="ri-arrow-down-line text-xs"></i>
          </div>
        </button>
      </div>

    </section>
  );
}